import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames';
import { DateRangePicker, DayPickerRangeController } from 'react-dates';
import 'react-dates/initialize';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import messages from '../../../locale/jsonMessages';
import { connect } from 'react-redux';
import { dateInternationalization, formatLocaleData, isRTL } from '../../../helpers/formatLocale';
import { dateFormat } from '../../../config';
import { CalendarIcon } from '../../Common/Assets/SvgIconComponent/CommonSvgIcon';
import s from 'react-dates/lib/css/_datepicker.css';
import c from '../../../styles/reactDatesCalendar.css';
import cs from '../../../components/commonStyle.css';
import d from './AdminDashboard.css';

const DateFilter = ({ onDateChange, locale }) => {
    const momentLocale = formatLocaleData(locale);
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days'));
    const [endDate, setEndDate] = useState(moment());
    const [focusedInput, setFocusedInput] = useState(null);
    const [filterOption, setFilterOption] = useState('last30Days');
    const [hasPendingCustomChange, setHasPendingCustomChange] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [isOpenDatePicker, setIsShowDatePicker] = useState(false);
    const [visibleMonth, setVisibleMonth] = useState(moment());
    const [smallDevice, setSmallDevice] = useState(false)
    const pickerWrapperRef = useRef(null);

    useEffect(() => {
        handleDateChange('last30Days');
    }, []);

    useEffect(() => {
        dateInternationalization(locale);
        moment.locale(momentLocale);
        setVisibleMonth((currentMonth) => (
            currentMonth ? moment(currentMonth).locale(momentLocale) : moment().locale(momentLocale)
        ));
    }, [locale, momentLocale]);

    const handleDateChange = (option) => {
        let newStartDate, newEndDate;
        if (option === 'last30Days') {
            newStartDate = moment().subtract(30, 'days');
            newEndDate = moment();
        } else if (option === 'last7Days') {
            newStartDate = moment().subtract(6, 'days');
            newEndDate = moment();
        } else if (option === 'last24Hours') {
            newStartDate = moment().subtract(24, 'hours');
            newEndDate = moment();
        } else {
            return;
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setFocusedInput(option === 'last30Days' ? 'startDate' : null);

        if (onDateChange) {
            onDateChange(newStartDate, newEndDate, option);
        }
    };

    const onDatesChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
        if (filterOption !== 'custom') {
            return;
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setHasPendingCustomChange(true);

    };

    const onFocusChange = (focused) => {
        if (!focused) {
            setFocusedInput(filterOption === 'custom' ? 'startDate' : null);
            return;
        }
        setFocusedInput(focused);
    };

    const isCustom = filterOption === 'custom';
    const isPreset = !isCustom;
    const canApply = !!startDate;
    const handleApply = () => {
        if (!onDateChange || !canApply) {
            return;
        }
        const appliedEndDate = endDate || startDate;
        setEndDate(appliedEndDate);
        onDateChange(startDate, appliedEndDate, filterOption);
        setHasPendingCustomChange(false);
        setFocusedInput(null);
        setShowPicker(false);
        setIsShowDatePicker(false);
    };
    const handleOutsideClick = useCallback(() => {
        setFocusedInput(null);
        setShowPicker(false);
        setIsShowDatePicker(false);
    }, []);

    const handleClear = () => {
        setFilterOption('last30Days');
        setHasPendingCustomChange(false);
        setIsShowDatePicker(false)
        setFocusedInput(null);
        setShowPicker(false);
        handleDateChange('last30Days');
    };

    const handlePresetClick = (option, { openPicker, showDatePicker } = {}) => {
        setFilterOption(option);
        setHasPendingCustomChange(false);
        if (option === 'custom') {
            const shouldClear = filterOption !== 'custom';
            if (shouldClear) {
                setStartDate(null);
                setEndDate(null);
                setFocusedInput('startDate');
                setVisibleMonth(moment().locale(momentLocale));
            } else {
                const hasStart = !!startDate;
                const hasEnd = !!endDate;
                if (!hasStart) {
                    setStartDate(null);
                }
                if (!hasEnd) {
                    setEndDate(null);
                }
                setFocusedInput(hasStart && !hasEnd ? 'endDate' : 'startDate');
                setVisibleMonth((startDate || endDate)
                    ? moment(startDate || endDate).locale(momentLocale)
                    : moment().locale(momentLocale));
            }
            setShowPicker(true);
            setIsShowDatePicker(true);
            return;
        }
        handleDateChange(option);
        if (openPicker) {
            setFocusedInput('startDate');
            setShowPicker(true);
        } else {
            setFocusedInput(null);
            setShowPicker(false);
        }
        showDatePicker ? setIsShowDatePicker(true) : setIsShowDatePicker(false)
    };

    const getCustomLabel = () => {
        if (startDate && endDate) {
            return `${moment(startDate).locale(momentLocale).format(dateFormat?.mediumDateFormat)} - ${moment(endDate).locale(momentLocale).format(dateFormat?.mediumDateFormat)}`;
        }
        return null;
    };

    const radioOptions = [
        { value: 'last30Days', messageKey: 'last30Days' },
        { value: 'last7Days', messageKey: 'last7Days' },
        { value: 'last24Hours', messageKey: 'last24Hours' },
        { value: 'custom', messageKey: 'custom', label: filterOption === 'custom' ? getCustomLabel() : null },
    ];
    const handleResize = useCallback(() => {
        const isBrowser = typeof window !== 'undefined';
        setSmallDevice(isBrowser ? window.matchMedia('(max-width: 767px)').matches : true);
    }, []);

    useEffect(() => {

        if (typeof window !== 'undefined') {
            handleResize();
            window.addEventListener('resize', handleResize);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, [handleResize]);

    useEffect(() => {
        if (!isOpenDatePicker) {
            return;
        }
        const handleDocumentMouseDown = (event) => {
            if (!pickerWrapperRef.current) {
                return;
            }
            if (pickerWrapperRef.current.contains(event.target)) {
                return;
            }
            handleOutsideClick();
        };
        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [isOpenDatePicker, handleOutsideClick]);

    return (
        <div className={d.dashboardDateFilterWrapper}>
            {
                <button
                    type="button"
                    className={cx(d.dateFilterSelectContainer, 'dateFilterSelectContainerRtl')}
                    onClick={() => handlePresetClick(filterOption, { openPicker: true, showDatePicker: true })}
                >
                    <CalendarIcon />
                    <span>
                        {filterOption === 'custom' ? (
                            <>{getCustomLabel() || <FormattedMessage {...messages.custom} />}</>
                        ) : (
                            <FormattedMessage {...messages[filterOption]} />
                        )}
                    </span>
                </button>

            }
            {
                isOpenDatePicker && <div
                    ref={pickerWrapperRef}
                    className={cx(d.dashboardDateFilterPickerAndButtonWrapper, 'dashboardDateFilterPickerAndButtonWrapperRtl')}
                >
                    <div className={d.dashboardDateFilterPickerInnerWrapper}>
                        <div className={cx(d.dashboardDateFilterButtonWrapper, 'dashboardDateFilterButtonWrapperRtl')}>
                            <div className={d.dashboardDateFilterButtonInnerWrapper}>
                                {radioOptions.map(({ value, messageKey, label }) => (
                                    <label key={value} className={d.dashboardDateFilterRadioLabel}>
                                        <input
                                            type="radio"
                                            name="dashboardDateRange"
                                            value={value}
                                            checked={filterOption === value}
                                            onChange={() => handlePresetClick(value, { openPicker: true })}
                                            className={d.dashboardDateFilterRadioInput}
                                        />
                                        <span className={d.dashboardDateFilterRadioText}>
                                            {label != null ? label : <FormattedMessage {...messages[messageKey]} />}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div className={d.dateFilterApplyAndClearButtonWrapper}>
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className={d.dateFilterClearLink}
                                    aria-label="Clear date range"
                                >
                                    <FormattedMessage {...messages.clear} />
                                </button>
                                <button
                                    type="button"
                                    className={cx(cs.btnPrimary, cs.btnSmall, d.dateFilterApplyButton)}
                                    onClick={handleApply}
                                    disabled={!canApply}
                                >
                                    <FormattedMessage {...messages.apply} />
                                </button>
                            </div>
                        </div>
                        {showPicker &&
                            <div className='dashboardDateFilterContainer'>
                                <DayPickerRangeController
                                    key={`${locale}-${smallDevice ? 33 : 42}-${filterOption}-${startDate?.valueOf?.() ?? 's'}-${endDate?.valueOf?.() ?? 'e'}`}
                                    focusedInput={focusedInput}
                                    startDate={startDate ? moment(startDate) : null}
                                    endDate={endDate ? moment(endDate) : null}
                                    minimumNights={0}
                                    hideKeyboardShortcutsPanel
                                    onDatesChange={onDatesChange}
                                    onFocusChange={onFocusChange}
                                    numberOfMonths={1}
                                    isOutsideRange={(day) => {
                                        const today = moment();
                                        const earliestAllowed = moment().subtract(2, 'years');
                                        if (day.isAfter(today, 'day')) {
                                            return true;
                                        }
                                        if (day.isBefore(earliestAllowed, 'day')) {
                                            return true;
                                        }
                                        if (isPreset && startDate && endDate) {
                                            return day.isBefore(startDate, 'day') || day.isAfter(endDate, 'day');
                                        }
                                        return false;
                                    }}
                                    transitionDuration={0}
                                    anchorDirection={isRTL(locale) ? 'right' : 'left'}
                                    isRTL={isRTL(locale)}
                                    initialVisibleMonth={() => (startDate || endDate) ? moment(startDate || endDate) : moment(visibleMonth).isValid() ? moment(visibleMonth) : moment()}
                                    onPrevMonthClick={(month) => setVisibleMonth(month)}
                                    onNextMonthClick={(month) => setVisibleMonth(month)}
                                    daySize={smallDevice ? 33 : 42}
                                />
                            </div>
                        }
                    </div>
                </div>
            }
        </div >
    );
};

DateFilter.propTypes = {
    onDateChange: PropTypes.func,
};

const mapState = (state) => ({
    locale: state.intl.locale
});
const mapDispatch = {

};

export default injectIntl(withStyles(s, c, cs, d)(connect(mapState, mapDispatch)(DateFilter)));
